import { useState, useRef } from "react";
import { toast } from "sonner";
import {
  Flower2, Leaf, Calendar, Loader2,
  Clock, Droplets, Scissors, AlertTriangle, RefreshCw,
  Sprout, Bug, ChevronDown, ChevronUp, Heart,
  Download, Palette
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useLanguage } from "@/context/LanguageContext";
import { SectionHeader } from "@/components/flower/SectionHeader";
import { InfoRow } from "@/components/flower/InfoRow";
import { ProductsSection } from "@/components/flower/ProductsSection";

// Flower Card Component
export const FlowerCard = ({ flower, index }) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const cardRef = useRef(null);
  const isFollaje = flower.tipo === "follaje" || flower.tipo === "foliage" || flower.tipo === "folhagem";

  const getSensibilidadColor = (nivel) => {
    switch(nivel?.toLowerCase()) {
      case 'alta': case 'high': return '#BC4749';
      case 'media': case 'medium': case 'média': return '#D4A373';
      case 'baja': case 'low': case 'baixa': return '#A3B18A';
      default: return '#6B705C';
    }
  };

  const handleDownload = async () => {
    if (!cardRef.current || isDownloading) return;

    setIsDownloading(true);

    // Expand card before capturing
    const wasOpen = isOpen;
    if (!wasOpen) setIsOpen(true);

    // Wait for expansion animation
    await new Promise(resolve => setTimeout(resolve, 300));

    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: '#F9F7F2',
        logging: false,
        useCORS: true
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });

      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(`${flower.nombre_comun.replace(/\s+/g, '_')}_ficha.pdf`);

      toast.success(t.downloadSuccess);
    } catch {
      toast.error(t.downloadError);
    } finally {
      if (!wasOpen) setIsOpen(false);
      setIsDownloading(false);
    }
  };

  return (
    <Card
      ref={cardRef}
      data-testid={`flower-card-${index}`}
      className={`flower-card fade-in-up stagger-${(index % 5) + 1} overflow-hidden`}
      style={{ opacity: 0 }}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start gap-4">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: isFollaje ? 'rgba(163, 177, 138, 0.2)' : 'rgba(212, 163, 115, 0.2)' }}
          >
            {isFollaje ? (
              <Leaf className="w-7 h-7" style={{ color: '#A3B18A' }} />
            ) : (
              <Flower2 className="w-7 h-7" style={{ color: '#D4A373' }} />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <CardTitle className="font-heading text-xl md:text-2xl mb-1" style={{ color: '#1A3C34' }}>
              {flower.nombre_comun}
            </CardTitle>
            {flower.nombre_cientifico && (
              <p className="font-accent text-sm" style={{ color: '#6B705C' }}>
                {flower.nombre_cientifico}
              </p>
            )}

            {flower.sinonimos_comerciales?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {flower.sinonimos_comerciales.map((sin) => (
                  <Badge key={sin} variant="outline" className="text-xs" style={{ borderColor: '#DAD7CD', color: '#6B705C' }}>
                    {sin}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col items-end gap-2">
            <Badge
              variant="outline"
              className="text-xs font-medium"
              style={{
                borderColor: isFollaje ? '#A3B18A' : '#D4A373',
                color: isFollaje ? '#A3B18A' : '#D4A373',
                backgroundColor: isFollaje ? 'rgba(163, 177, 138, 0.1)' : 'rgba(212, 163, 115, 0.1)'
              }}
            >
              {flower.tipo}
            </Badge>
            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all hover:scale-105"
              style={{
                backgroundColor: 'rgba(163, 177, 138, 0.2)',
                color: '#1A3C34'
              }}
              data-testid={`download-card-${index}`}
            >
              {isDownloading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Download className="w-3 h-3" />
              )}
              <span className="hidden sm:inline">{t.downloadCard}</span>
            </button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="p-3 rounded-lg col-span-2" style={{ backgroundColor: 'rgba(212, 163, 115, 0.1)' }}>
            <div className="flex items-center gap-1 mb-1">
              <Palette className="w-3 h-3" style={{ color: '#D4A373' }} />
              <p className="font-body text-xs font-medium" style={{ color: '#6B705C' }}>{t.availableColorsLabel}</p>
            </div>
            <div className="flex flex-wrap gap-1">
              {flower.colores_disponibles?.map((color, i) => (
                <Badge key={color} className="tag-color text-xs" data-testid={`color-tag-${index}-${i}`}>
                  {color}
                </Badge>
              ))}
            </div>
          </div>

          <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(163, 177, 138, 0.1)' }}>
            <p className="font-body text-xs font-medium mb-1" style={{ color: '#6B705C' }}>{t.season}</p>
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" style={{ color: '#A3B18A' }} />
              <span className="font-body text-xs" style={{ color: '#1A3C34' }}>{flower.temporada}</span>
            </div>
          </div>

          {flower.vida_florero && (
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(26, 60, 52, 0.05)' }}>
              <p className="font-body text-xs font-medium mb-1" style={{ color: '#6B705C' }}>{t.vasLifeLabel}</p>
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" style={{ color: '#1A3C34' }} />
                <span className="font-body text-xs font-semibold" style={{ color: '#1A3C34' }}>{flower.vida_florero}</span>
              </div>
            </div>
          )}
        </div>

        {flower.riesgos?.sensibilidad_etileno && (
          <div className="p-3 rounded-lg mb-4 flex items-center justify-between" style={{ backgroundColor: 'rgba(188, 71, 73, 0.05)' }}>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" style={{ color: getSensibilidadColor(flower.riesgos.sensibilidad_etileno) }} />
              <span className="font-body text-xs" style={{ color: '#1A3C34' }}>
                {t.ethyleneSensitivity}:
              </span>
            </div>
            <Badge
              className="text-xs"
              style={{
                backgroundColor: getSensibilidadColor(flower.riesgos.sensibilidad_etileno),
                color: 'white'
              }}
            >
              {flower.riesgos.sensibilidad_etileno}
            </Badge>
          </div>
        )}

        {flower.sustitutos?.length > 0 && (
          <div className="p-4 rounded-lg mb-4" style={{ backgroundColor: 'rgba(163, 177, 138, 0.15)', border: '1px dashed #A3B18A' }}>
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw className="w-4 h-4" style={{ color: '#1A3C34' }} />
              <span className="font-heading text-sm font-semibold" style={{ color: '#1A3C34' }}>
                {t.recommendedSubstitutes}
              </span>
            </div>
            <p className="font-body text-xs mb-2" style={{ color: '#6B705C' }}>
              {t.ifNotFound} {flower.nombre_comun}{t.youCanUse}
            </p>
            <div className="flex flex-wrap gap-2">
              {flower.sustitutos.map((sub) => (
                <Badge key={sub} className="text-xs px-3 py-1" style={{ backgroundColor: '#A3B18A', color: 'white' }}>
                  {sub}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {flower.productos_recomendados && (
          <ProductsSection productos={flower.productos_recomendados} />
        )}

        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger className="w-full mt-4" data-testid={`expand-card-${index}`}>
            <div
              className="flex items-center justify-center gap-2 py-2 rounded-lg transition-all hover:bg-opacity-80"
              style={{ backgroundColor: 'rgba(26, 60, 52, 0.05)' }}
            >
              <span className="font-body text-sm font-medium" style={{ color: '#1A3C34' }}>
                {isOpen ? t.viewLessDetails : t.viewCareAndRisks}
              </span>
              {isOpen ? (
                <ChevronUp className="w-4 h-4" style={{ color: '#1A3C34' }} />
              ) : (
                <ChevronDown className="w-4 h-4" style={{ color: '#1A3C34' }} />
              )}
            </div>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <div className="pt-4 space-y-4">
              {flower.factores_vida && (
                <div>
                  <SectionHeader icon={Heart} title={t.vasLifeFactors} color="#D4A373" />
                  <p className="font-body text-xs pl-6" style={{ color: '#1A3C34' }}>
                    {flower.factores_vida}
                  </p>
                </div>
              )}

              <Separator style={{ backgroundColor: '#DAD7CD' }} />

              {flower.cuidados_postcosecha && (
                <div>
                  <SectionHeader icon={Scissors} title={t.postHarvestCare} color="#1A3C34" />
                  <div className="pl-6 space-y-1">
                    <InfoRow icon={Scissors} label={t.stemCut} value={flower.cuidados_postcosecha.recorte_tallo} />
                    <InfoRow icon={Leaf} label={t.foliageCleaning} value={flower.cuidados_postcosecha.limpieza_follaje} />
                    <InfoRow icon={Droplets} label={t.hydrationLabel} value={flower.cuidados_postcosecha.hidratacion} />
                    {flower.cuidados_postcosecha.compatibilidades?.length > 0 && (
                      <div className="pt-2">
                        <p className="font-body text-xs font-medium mb-1" style={{ color: '#6B705C' }}>
                          {t.compatibleInArrangements}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {flower.cuidados_postcosecha.compatibilidades.map((comp) => (
                            <Badge key={comp} variant="outline" className="text-xs" style={{ borderColor: '#A3B18A', color: '#1A3C34' }}>
                              {comp}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {flower.cuidados_postcosecha && flower.riesgos && (
                <Separator style={{ backgroundColor: '#DAD7CD' }} />
              )}

              {flower.riesgos && (
                <div>
                  <SectionHeader icon={AlertTriangle} title={t.handlingRisks} color="#BC4749" />
                  <div className="pl-6 grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="p-2 rounded" style={{ backgroundColor: 'rgba(188, 71, 73, 0.05)' }}>
                      <p className="font-body text-xs font-medium" style={{ color: '#6B705C' }}>{t.wilting}</p>
                      <p className="font-body text-xs" style={{ color: '#1A3C34' }}>{flower.riesgos.propension_marchitez}</p>
                    </div>
                    <div className="p-2 rounded" style={{ backgroundColor: 'rgba(188, 71, 73, 0.05)' }}>
                      <p className="font-body text-xs font-medium" style={{ color: '#6B705C' }}>{t.staining}</p>
                      <p className="font-body text-xs" style={{ color: '#1A3C34' }}>{flower.riesgos.manchado}</p>
                    </div>
                    <div className="p-2 rounded md:col-span-2" style={{ backgroundColor: 'rgba(188, 71, 73, 0.05)' }}>
                      <p className="font-body text-xs font-medium" style={{ color: '#6B705C' }}>{t.dehydration}</p>
                      <p className="font-body text-xs" style={{ color: '#1A3C34' }}>{flower.riesgos.deshidratacion}</p>
                    </div>
                  </div>
                </div>
              )}

              {flower.cuidados_jardin && (
                <>
                  <Separator style={{ backgroundColor: '#DAD7CD' }} />
                  <div>
                    <SectionHeader icon={Sprout} title={t.gardenGuide} color="#A3B18A" />
                    <div className="pl-6 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="p-2 rounded" style={{ backgroundColor: 'rgba(163, 177, 138, 0.1)' }}>
                          <p className="font-body text-xs font-medium" style={{ color: '#6B705C' }}>{t.light}</p>
                          <p className="font-body text-xs" style={{ color: '#1A3C34' }}>{flower.cuidados_jardin.luz}</p>
                        </div>
                        <div className="p-2 rounded" style={{ backgroundColor: 'rgba(163, 177, 138, 0.1)' }}>
                          <p className="font-body text-xs font-medium" style={{ color: '#6B705C' }}>{t.watering}</p>
                          <p className="font-body text-xs" style={{ color: '#1A3C34' }}>{flower.cuidados_jardin.riego}</p>
                        </div>
                        <div className="p-2 rounded" style={{ backgroundColor: 'rgba(163, 177, 138, 0.1)' }}>
                          <p className="font-body text-xs font-medium" style={{ color: '#6B705C' }}>{t.soil}</p>
                          <p className="font-body text-xs" style={{ color: '#1A3C34' }}>{flower.cuidados_jardin.suelo}</p>
                        </div>
                        <div className="p-2 rounded" style={{ backgroundColor: 'rgba(163, 177, 138, 0.1)' }}>
                          <p className="font-body text-xs font-medium" style={{ color: '#6B705C' }}>{t.bloomingSeason}</p>
                          <p className="font-body text-xs" style={{ color: '#1A3C34' }}>{flower.cuidados_jardin.epoca_floracion}</p>
                        </div>
                      </div>

                      {flower.cuidados_jardin.plagas_enfermedades?.length > 0 && (
                        <div className="p-2 rounded" style={{ backgroundColor: 'rgba(188, 71, 73, 0.05)' }}>
                          <div className="flex items-center gap-1 mb-1">
                            <Bug className="w-3 h-3" style={{ color: '#BC4749' }} />
                            <p className="font-body text-xs font-medium" style={{ color: '#BC4749' }}>{t.pestsAndDiseases}</p>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {flower.cuidados_jardin.plagas_enfermedades.map((plaga) => (
                              <Badge key={plaga} variant="outline" className="text-xs" style={{ borderColor: '#BC4749', color: '#BC4749' }}>
                                {plaga}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};
